import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sandbox portal — shown after a sandbox user logs in.
 * Renders role-specific sections, all branded with the prospect's property name.
 * Data fetched via /api/sandbox/{slot}/data/* endpoints (sandbox JWT).
 */
export default function SandboxPortalPage() {
  const { slot } = useParams();
  const navigate = useNavigate();
  const raw = localStorage.getItem(`sandbox_session_${slot}`);
  const session = raw ? JSON.parse(raw) : null;
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [items, setItems] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [counts, setCounts] = useState([]);
  const [receiving, setReceiving] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      navigate(`/sandbox/${slot}`);
      return;
    }
    const h = { Authorization: `Bearer ${session.token}` };
    (async () => {
      try {
        const [s, v, p, i, l, c, r] = await Promise.all([
          axios.get(`${API}/sandbox/${slot}/data/summary`, { headers: h }),
          axios.get(`${API}/sandbox/${slot}/data/vendors`, { headers: h }),
          axios.get(`${API}/sandbox/${slot}/data/purchase-orders`, { headers: h }),
          axios.get(`${API}/sandbox/${slot}/data/items`, { headers: h }),
          axios.get(`${API}/sandbox/${slot}/data/low-stock`, { headers: h }),
          axios.get(`${API}/sandbox/${slot}/data/inventory-counts`, { headers: h }),
          axios.get(`${API}/sandbox/${slot}/data/receiving-records`, { headers: h }),
        ]);
        setSummary(s.data);
        setVendors(v.data.vendors || []);
        setPos(p.data.purchase_orders || []);
        setItems(i.data.items || []);
        setLowStock(l.data.alerts || []);
        setCounts(c.data.counts || []);
        setReceiving(r.data.receiving_records || []);
      } catch (err) {
        toast.error("Failed to load sandbox data. Session may have expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slot]);

  if (!session) return null;

  const handleSignOut = () => {
    localStorage.removeItem(`sandbox_session_${slot}`);
    navigate(`/sandbox/${slot}`);
  };

  const role = session.user.role;
  const propertyName = summary?.property_name || session.property_name;
  const logoUrl = summary?.logo_url || session.logo_url;

  const downloadPoPdf = async (poId) => {
    try {
      const r = await axios.get(`${API}/sandbox/${slot}/po/${poId}/pdf`, {
        headers: { Authorization: `Bearer ${session.token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PO-${poId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download PO PDF");
    }
  };

  const downloadGrnPdf = async (poId) => {
    try {
      const r = await axios.get(`${API}/sandbox/${slot}/grn/${poId}/pdf`, {
        headers: { Authorization: `Bearer ${session.token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receiving-${poId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download Receiving Report PDF");
    }
  };

  // Role visibility map — which sections each role sees prominently.
  const sees = {
    vendors:    ["purchasing_manager", "buyer", "financial_controller", "vendor"].includes(role),
    pos:        ["purchasing_manager", "buyer", "department_head", "financial_controller",
                 "receiver", "vendor", "freight_forwarder"].includes(role),
    items:      ["purchasing_manager", "storekeeper", "buyer", "internal_requester"].includes(role),
    low_stock:  ["purchasing_manager", "storekeeper", "financial_controller"].includes(role),
    counts:     ["purchasing_manager", "storekeeper", "financial_controller"].includes(role),
    receiving:  ["purchasing_manager", "receiver", "financial_controller", "freight_forwarder"].includes(role),
  };

  const roleLabel = {
    purchasing_manager: "Purchasing Manager",
    buyer: "Buyer",
    department_head: "Department Head",
    financial_controller: "Financial Controller",
    vendor: "Vendor",
    freight_forwarder: "Freight Forwarder",
    receiver: "Receiver",
    storekeeper: "Storekeeper",
    internal_requester: "Internal Requester",
  }[role] || role;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Branded header — property prominent, HERChain secondary */}
      <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {logoUrl && (
            <img
              src={`${BACKEND_URL}${logoUrl}`}
              alt=""
              className="h-10 max-w-[140px] object-contain"
              data-testid="sandbox-header-logo"
            />
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-800" data-testid="sandbox-header-property">
                {propertyName}
              </h1>
              <span
                className="bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                data-testid="sandbox-header-badge"
              >
                SANDBOX
              </span>
            </div>
            <p className="text-xs text-slate-400">Powered by HERChain Solutions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600" data-testid="sandbox-header-user">
            {session.user.full_name} · {roleLabel}
          </span>
          <button
            onClick={handleSignOut}
            className="text-sm text-slate-500 hover:text-slate-700 border border-slate-300 rounded-md px-3 py-1"
            data-testid="sandbox-signout-btn"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8">
        {/* Welcome banner with property name reference */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-800 mb-1" data-testid="sandbox-portal-welcome">
            {roleLabel} · {propertyName}
          </h2>
          <p className="text-slate-500 text-sm" data-testid="sandbox-portal-subtitle">
            Sandbox environment — {summary?.tax_type || "—"} {summary?.tax_rate ?? ""}
            {summary?.tax_type && summary?.tax_rate ? "%" : ""}
            {summary?.expires_at && ` · expires ${new Date(summary.expires_at).toLocaleString()}`}
          </p>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6" data-testid="sandbox-summary-cards">
            {Object.entries(summary.counts || {}).map(([k, v]) => (
              <div key={k} className="bg-white rounded-lg border border-slate-200 p-4">
                <p className="text-xs uppercase tracking-wider text-slate-400">{k.replace("_", " ")}</p>
                <p className="text-2xl font-bold text-slate-800" data-testid={`sandbox-count-${k}`}>{v}</p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-slate-500 p-6" data-testid="sandbox-portal-loading">Loading demo data…</div>
        ) : (
          <div className="space-y-6">
            {/* Low stock alerts */}
            {sees.low_stock && lowStock.length > 0 && (
              <SectionCard title={`Low stock alerts (${lowStock.length})`} testId="sandbox-section-low-stock">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-xs uppercase tracking-wider">
                    <tr><th className="text-left py-1">Item</th><th className="text-right">On hand</th><th className="text-right">Par</th></tr>
                  </thead>
                  <tbody>
                    {lowStock.map((a) => (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="py-2">{a.item_name}</td>
                        <td className="text-right text-red-600 font-semibold">{a.on_hand}</td>
                        <td className="text-right">{a.par_level}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SectionCard>
            )}

            {/* Purchase orders */}
            {sees.pos && pos.length > 0 && (
              <SectionCard title={`Purchase Orders (${pos.length})`} testId="sandbox-section-pos">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-1">PO #</th>
                      <th className="text-left">Vendor</th>
                      <th className="text-left">Status</th>
                      <th className="text-right">Total</th>
                      <th className="text-right">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pos.map((p) => (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="py-2 font-mono text-xs">{p.po_number}</td>
                        <td>{p.vendor_name}</td>
                        <td><StatusPill status={p.status} /></td>
                        <td className="text-right">{p.currency} {(p.total_value ?? 0).toFixed(2)}</td>
                        <td className="text-right space-x-2">
                          <button
                            onClick={() => downloadPoPdf(p.id)}
                            className="text-slate-600 underline hover:text-slate-800 text-xs"
                            data-testid={`sandbox-po-pdf-${p.id}`}
                          >
                            PO PDF
                          </button>
                          {p.status === "closed" && (
                            <button
                              onClick={() => downloadGrnPdf(p.id)}
                              className="text-slate-600 underline hover:text-slate-800 text-xs"
                              data-testid={`sandbox-grn-pdf-${p.id}`}
                            >
                              GRN PDF
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SectionCard>
            )}

            {/* Vendors */}
            {sees.vendors && vendors.length > 0 && (
              <SectionCard title={`Vendors (${vendors.length})`} testId="sandbox-section-vendors">
                <ul className="divide-y divide-slate-100 text-sm">
                  {vendors.map((v) => (
                    <li key={v.id} className="py-2 flex justify-between">
                      <span className="font-medium text-slate-800">{v.company_name}</span>
                      <span className="text-xs text-slate-500">{v.country} · {v.vendor_type}</span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}

            {/* Item master */}
            {sees.items && items.length > 0 && (
              <SectionCard title={`Item Master (${items.length})`} testId="sandbox-section-items">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {items.slice(0, 12).map((it) => (
                    <div key={it.id} className="flex justify-between bg-slate-50 rounded px-2 py-1">
                      <span className="truncate mr-2">{it.item_name}</span>
                      <span className="text-slate-500 text-xs">{it.cost_tag}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Receiving records */}
            {sees.receiving && receiving.length > 0 && (
              <SectionCard title={`Receiving Records (${receiving.length})`} testId="sandbox-section-receiving">
                <ul className="divide-y divide-slate-100 text-sm">
                  {receiving.map((r) => (
                    <li key={r.id} className="py-2 flex justify-between">
                      <span className="font-mono text-xs">{r.po_number}</span>
                      <span className="text-xs text-slate-500">{r.status}</span>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}

            {/* Inventory counts */}
            {sees.counts && counts.length > 0 && (
              <SectionCard title={`Inventory Counts (${counts.length})`} testId="sandbox-section-counts">
                <ul className="divide-y divide-slate-100 text-sm">
                  {counts.map((c) => (
                    <li key={c.id} className="py-2 flex justify-between">
                      <div>
                        <div className="font-mono text-xs">{c.count_number}</div>
                        <div className="text-xs text-slate-500">{c.count_type} · {c.total_items} items</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Variance</div>
                        <div className={`font-semibold ${c.total_variance_cost < 0 ? "text-red-600" : "text-slate-700"}`}>
                          {c.total_variance_cost?.toFixed(2)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}
          </div>
        )}

        {/* HERChain footer */}
        <p className="text-center text-xs text-slate-400 mt-10">
          Powered by <span className="font-semibold text-slate-500">HERChain Solutions</span>
        </p>
      </main>
    </div>
  );
}

function SectionCard({ title, testId, children }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid={testId}>
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">{title}</h3>
      {children}
    </section>
  );
}

function StatusPill({ status }) {
  const map = {
    closed: "bg-slate-100 text-slate-700 border-slate-300",
    in_transit: "bg-blue-100 text-blue-700 border-blue-300",
    pending_approval: "bg-amber-100 text-amber-700 border-amber-300",
    delivered: "bg-emerald-100 text-emerald-700 border-emerald-300",
  };
  const c = map[status] || "bg-slate-100 text-slate-600 border-slate-300";
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${c}`}>
      {status?.replace("_", " ")}
    </span>
  );
}
